import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
} from 'react'

import withConfig from '../../../wrappers/withConfig'
import { APIRequestContext } from '../../../wrappers/APIRequestContext'
import actions from '../../../../utils/submissions/actions'
import { UserContext } from '../../../wrappers/UserContext'
import toast from '../../../elem/Toast'

const TableContext = createContext(null)

const TableContextProvider = ({ config, children }) => {
    const { authenticatedFetch } = useContext(APIRequestContext)
    const { user } = useContext(UserContext)
    const [loading, setLoading] = useState(true)
    const [bulkSubmitLoading, setBulkSubmitLoading] = useState(false)
    const [uploads, setUploads] = useState(null)
    const [selectedUploads, setSelectedUploads] = useState([])
    const { API_URL, UPLOAD_ID_COLUMN } = config
    const [confirmDeleteModalData, setConfirmDeleteModalData] = useState({
        display: false,
        uploadId: null,
        submittingEDD: null,
    })
    const [bulkComments, setBulkComments] = useState('')
    const [displayBulkApproveModal, setDisplayBulkApproveModal] = useState(
        false
    )
    const [displayBulkDenyModal, setDisplayBulkDenyModal] = useState(false)

    const resolveBulkModalState = (buttonAction, state) => {
        setBulkComments('')
        if (buttonAction === 'Approve') setDisplayBulkApproveModal(state)
        else setDisplayBulkDenyModal(state)
    }

    // fetch uploads
    useEffect(() => {
        actions.getTableData(
            authenticatedFetch,
            API_URL,
            setLoading,
            setUploads
        )
    }, [])

    // bulk submit
    const bulkSubmit = useCallback(
        action => {
            if (bulkComments && bulkComments.includes(`'`)) {
                toast({
                    level: 'error',
                    message: `Comments cannot contain an apostrophe`,
                    alert: true,
                })
                return
            }
            if (action === 'Deny' && !bulkComments) {
                toast({
                    level: 'error',
                    message: `Comments are required for denied submissions.`,
                    alert: true,
                })
                return
            }
            const selectedUploadIds = selectedUploads.map(x => ({
                uploadId: x['Form ID'],
                formType: x['Form Type'],
            }))
            setBulkSubmitLoading(true)
            actions.bulkSubmit(
                action,
                authenticatedFetch,
                API_URL,
                selectedUploadIds,
                bulkComments,
                user.profile.name,
                e => {
                    resolveBulkModalState(action, false)
                    setBulkSubmitLoading(false)
                },
                () => {
                    let successMessage
                    switch (action) {
                        case 'Approve':
                            successMessage =
                                'All selected forms have been approved'
                            break
                        case 'Deny':
                            successMessage =
                                'All selected forms have been denied'
                            break
                        default:
                            successMessage =
                                'All selected forms have been submitted and can now be viewed in the Submitted Forms tab'
                            break
                    }
                    toast({
                        level: 'info',
                        message: successMessage,
                        alert: true,
                    })
                    actions.getTableData(
                        authenticatedFetch,
                        API_URL,
                        setLoading,
                        setUploads
                    )
                    resolveBulkModalState(action, false)
                    setBulkSubmitLoading(false)
                }
            )
        },
        [selectedUploads, user, bulkComments]
    )

    const deleteFunction = useCallback(
        recordData => {
            let url = `${API_URL}/upload/delete?uploadId=${recordData.uploadId}`
            if (recordData.submittingEDD) {
                url += `&submittingEDD=${encodeURIComponent(
                    recordData.submittingEDD
                )}`
            }

            authenticatedFetch(url, {
                method: 'DELETE',
            })
                .then(async response => {
                    if (response.ok) {
                        return
                    } else {
                        const error = await response.text()
                        throw new Error(error)
                    }
                })
                .then(() => {
                    setUploads(previousUploads => ({
                        ...previousUploads,
                        incomplete: previousUploads.incomplete.filter(
                            x => x['Form ID'] !== recordData.uploadId
                        ),
                    }))
                    toast({
                        level: 'success',
                        message: 'Delete operation completed successfully.',
                        alert: true,
                    })
                })
                .catch(e => {
                    toast({
                        level: 'error',
                        message:
                            'Submission Delete: ' +
                            (e.message
                                ? e.message
                                : 'Unable to connect to the server. Please try again later.'),
                    })
                })
                .finally(() => {
                    setConfirmDeleteModalData({
                        display: false,
                        uploadId: null,
                        submittingEDD: null,
                    })
                })
        },
        [
            API_URL,
            authenticatedFetch,
            setUploads,
            setConfirmDeleteModalData,
            UPLOAD_ID_COLUMN,
        ]
    )
    return (
        <TableContext.Provider
            value={{
                uploads,
                setUploads,
                selectedUploads,
                setSelectedUploads,
                loading,
                confirmDeleteModalData,
                setConfirmDeleteModalData,
                bulkSubmit,
                bulkSubmitLoading,
                deleteFunction,
                bulkComments,
                setBulkComments,
                displayBulkApproveModal,
                displayBulkDenyModal,
                resolveBulkModalState,
            }}
        >
            {children}
        </TableContext.Provider>
    )
}

export { TableContext }
export default withConfig(TableContextProvider)
